/**
 * Custom toast utilities with copy functionality for errors
 */
import { toast as sonnerToast } from 'sonner';
import { Copy, Check } from 'lucide-react';
import { createElement, useState } from 'react';
import fr from '../i18n/locales/fr.js';

/**
 * Translate backend error messages from English to French
 * Uses the backendMessages mapping from fr.js translations
 */
export const translateBackendError = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return errorMessage;
  }

  const backendMessages = fr.errors?.backendMessages || {};

  // Check for exact match
  if (backendMessages[errorMessage]) {
    return backendMessages[errorMessage];
  }

  // Check for partial match (for messages with dynamic content)
  for (const [english, french] of Object.entries(backendMessages)) {
    if (errorMessage.toLowerCase().includes(english.toLowerCase())) {
      return errorMessage.replace(new RegExp(english, 'gi'), french);
    }
  }

  return errorMessage;
};

/**
 * Copy button component for toast messages
 */
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return createElement('button', {
    onClick: handleCopy,
    className: 'ml-2 p-1 hover:bg-white/10 rounded transition-colors inline-flex items-center',
    title: 'Copier le message',
    style: {
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: copied ? '#22c55e' : '#9ca3af',
      minWidth: '24px',
      height: '24px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, copied
    ? createElement(Check, { size: 14 })
    : createElement(Copy, { size: 14 })
  );
};

/**
 * Error message component with copy button
 */
const ErrorMessage = ({ message }) => {
  return createElement('div', {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '8px',
      width: '100%'
    }
  }, [
    createElement('span', {
      key: 'text',
      style: {
        flex: 1,
        wordBreak: 'break-word',
        userSelect: 'text'
      }
    }, message),
    createElement(CopyButton, { key: 'copy', text: message })
  ]);
};

/**
 * Custom toast wrapper with copy functionality for errors
 */
export const toast = {
  // Success toast (standard)
  success: (message, options) => {
    return sonnerToast.success(message, options);
  },

  // Error toast with copy button
  error: (message, options = {}) => {
    const errorMessage = typeof message === 'string' ? message : String(message);

    return sonnerToast.error(createElement(ErrorMessage, { message: errorMessage }), {
      ...options,
      duration: options.duration || 8000, // Longer duration for errors
      style: {
        ...options.style,
        minWidth: '300px'
      }
    });
  },

  // Info toast (standard)
  info: (message, options) => {
    return sonnerToast.info(message, options);
  },

  // Warning toast with copy button
  warning: (message, options = {}) => {
    const warningMessage = typeof message === 'string' ? message : String(message);

    return sonnerToast.warning(createElement(ErrorMessage, { message: warningMessage }), {
      ...options,
      duration: options.duration || 6000
    });
  },

  // Loading toast (standard)
  loading: (message, options) => {
    return sonnerToast.loading(message, options);
  },

  // Promise toast (standard)
  promise: (promise, options) => {
    return sonnerToast.promise(promise, options);
  },

  // Dismiss toast
  dismiss: (toastId) => {
    return sonnerToast.dismiss(toastId);
  },

  // Custom toast
  custom: (content, options) => {
    return sonnerToast.custom(content, options);
  },

  // Message toast (standard)
  message: (message, options) => {
    return sonnerToast.message(message, options);
  }
};

export default toast;
